import { type PmpDealType, pmpDealType } from '@openx/types/deal';
import type { ConstValues } from '@openx/types/helpers';
import type { I18nTranslate } from '@openx/types/translations';
import { DV360_DEMAND_PARTNER_ID, TTD_DEMAND_PARTNER_ID, XANDR_DEMAND_PARTNER_ID } from '@openx/utils/services/config';

export const DemandPartnerType = {
  DEFAULT: 'DEFAULT',
  DV360: 'DV360',
  TTD: 'TTD',
  XANDR: 'XANDR',
} as const;
export type DemandPartnerType = ConstValues<typeof DemandPartnerType>;

export interface IParticipantFunctions {
  getDemandPartnerType: (demandPartner: string | null, dealType?: PmpDealType | null) => DemandPartnerType;
  getTranslationKeyForBuyerSeatInfo: (
    demandPartnerType?: DemandPartnerType,
    t?: I18nTranslate,
    isTTDPartnerAndGuaranteedDealType?: boolean
  ) => string | null;
}

const ttdAvailableDealTypes: PmpDealType[] = [
  pmpDealType.PreferredDeal,
  pmpDealType.PrivateAuction,
  pmpDealType.Guaranteed,
];

const participantFunctionsDefault: IParticipantFunctions = {
  getDemandPartnerType: (demandPartner, dealType) => {
    if (demandPartner === TTD_DEMAND_PARTNER_ID && dealType && ttdAvailableDealTypes.includes(dealType)) {
      return DemandPartnerType.TTD;
    } else if (demandPartner === DV360_DEMAND_PARTNER_ID) {
      return DemandPartnerType.DV360;
    } else if (demandPartner === XANDR_DEMAND_PARTNER_ID) {
      return DemandPartnerType.XANDR;
    } else {
      return DemandPartnerType.DEFAULT;
    }
  },

  getTranslationKeyForBuyerSeatInfo: (demandPartnerType, t, isTTDPartnerAndGuaranteedDealType) => {
    switch (demandPartnerType) {
      case DemandPartnerType.TTD:
        if (isTTDPartnerAndGuaranteedDealType) {
          return t
            ? t(
                "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown."
              )
            : "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown.";
        } else {
          return t
            ? t(
                "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown. If more than one buyer should be included, use additional 'Additional Buyers' dropdown."
              )
            : "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown. If more than one buyer should be included, use additional 'Additional Buyers' dropdown.";
        }

      case DemandPartnerType.DV360:
        return t
          ? t(
              'OpenX automatically sync the deals through DV360 Deal Sync API. Some fields may have become mandatory for the sake of syncing deals properly.'
            )
          : 'OpenX automatically sync the deals through DV360 Deal Sync API. Some fields may have become mandatory for the sake of syncing deals properly.';

      case DemandPartnerType.XANDR:
        return t
          ? t(
              'For selected DSP, adding their DSP Seat ID is now required in order for the deals to be synced automatically with their system. DSP Seat IDs are listed within the dropdown by OpenX buyer name and the DSP’s Seat ID (BuyerInc > 12345).'
            )
          : 'For selected DSP, adding their DSP Seat ID is now required in order for the deals to be synced automatically with their system. DSP Seat IDs are listed within the dropdown by OpenX buyer name and the DSP’s Seat ID (BuyerInc > 12345).';

      default:
        return null;
    }
  },
};

export const participantFunctionsDSP: IParticipantFunctions = {
  getDemandPartnerType: (demandPartner, dealType) => {
    if (demandPartner === TTD_DEMAND_PARTNER_ID && dealType && ttdAvailableDealTypes.includes(dealType)) {
      return DemandPartnerType.TTD;
    } else if (demandPartner === DV360_DEMAND_PARTNER_ID) {
      return DemandPartnerType.DV360;
    } else if (demandPartner === XANDR_DEMAND_PARTNER_ID) {
      return DemandPartnerType.XANDR;
    } else {
      return DemandPartnerType.DEFAULT;
    }
  },

  getTranslationKeyForBuyerSeatInfo: (demandPartnerType, t, isTTDPartnerAndGuaranteedDealType) => {
    switch (demandPartnerType) {
      case DemandPartnerType.TTD:
        if (isTTDPartnerAndGuaranteedDealType) {
          return t
            ? t(
                "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown."
              )
            : "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown.";
        } else {
          return t
            ? t(
                "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown. If more than one buyer should be included, use additional 'Additional Buyers' dropdown."
              )
            : "OpenX automatically pushes deals through The Trade Desk Price Discovery & Provisioning API now. Select the Buyer by OpenX buyer name and the TTD buyer's ID in 'Main Buyer' dropdown. If more than one buyer should be included, use additional 'Additional Buyers' dropdown.";
        }

      case DemandPartnerType.DV360:
        return t
          ? t(
              'OpenX automatically sync the deals through DV360 Deal Sync API. Some fields may have become mandatory for the sake of syncing deals properly.'
            )
          : 'OpenX automatically sync the deals through DV360 Deal Sync API. Some fields may have become mandatory for the sake of syncing deals properly.';

      case DemandPartnerType.XANDR:
        return t
          ? t(
              'For selected DSP, adding their DSP Seat ID is now required in order for the deals to be synced automatically with their system. DSP Seat IDs are listed within the dropdown by OpenX buyer name and the DSP’s Seat ID (BuyerInc > 12345).'
            )
          : 'For selected DSP, adding their DSP Seat ID is now required in order for the deals to be synced automatically with their system. DSP Seat IDs are listed within the dropdown by OpenX buyer name and the DSP’s Seat ID (BuyerInc > 12345).';

      default:
        return null;
    }
  },
};

export const participantFunctionsPublisher: IParticipantFunctions = {
  getDemandPartnerType: participantFunctionsDefault.getDemandPartnerType,
  getTranslationKeyForBuyerSeatInfo: participantFunctionsDefault.getTranslationKeyForBuyerSeatInfo,
};

export const participantFunctionsInternal: IParticipantFunctions = {
  getDemandPartnerType: participantFunctionsDefault.getDemandPartnerType,
  getTranslationKeyForBuyerSeatInfo: participantFunctionsDefault.getTranslationKeyForBuyerSeatInfo,
};
